import { eventUserInfoEventMembers } from '@goldcast/api/backend';

import { initializeFeatureFlags } from '@/services/featureFlag.service';
import loggerService from '@/services/logger.service';
import store from '@/store';
import { isInsideEventPage, isPreEventEntryPage } from '@/utils/url';

/**
 * Shape returned by GET /event/event-members/event-user-info/ (a single object).
 **/
export interface EventUserInfoResponse {
  id: string;
  event_role?: number;
  event?: string;
  user: {
    id: string;
    email: string;
    display_email: string;
    first_name?: string;
    last_name?: string;
    [key: string]: unknown;
  };
  ticket_codes: unknown[];
  registeredToEvent: boolean;
  isOrganizer: boolean;
  isSpecialUser: boolean;
  event_organization: string | null;
  agendaFavorites: unknown[];
  name: string | null;
  blocked: boolean;
  [key: string]: unknown;
}

let eventUserInfoRequest: Promise<unknown> | null = null;
/** Clear the shared request, e.g. on sign-out / user switch. */
export function resetEventUserInfo(): void {
  eventUserInfoRequest = null;
}

export interface EventUserInfoOptions<T> {
  eventId?: string | null;
  legacy: () => Promise<T>;
  mapResponse: (info: EventUserInfoResponse) => T;
  isInitialLoad?: boolean;
}

// The flag reaches the store asynchronously at boot, so ensure feature flags are
// initialized before deciding — otherwise the first pre-entry read races ahead of
// the flag and falls back to the legacy endpoint.
async function isConsolidationEnabled(): Promise<boolean> {
  if (!store.state.event.useEventApiConsolidation) {
    await initializeFeatureFlags(false).catch(() => undefined);
  }
  return store.state.event.useEventApiConsolidation;
}

/**
 * Routes a self-user read through the consolidated event-user-info endpoint
 * before event entry (when EVENT_API_CONSOLIDATION is on), and through the
 * original endpoint otherwise.
 */
export async function getEventUserInfo<T>({
  eventId,
  legacy,
  mapResponse,
  isInitialLoad
}: EventUserInfoOptions<T>): Promise<T> {
  const isConsolidatablePage = isPreEventEntryPage() || (isInsideEventPage() && !!isInitialLoad);
  if (!isConsolidatablePage || !(await isConsolidationEnabled())) {
    return legacy();
  }

  // Fetch once and share across all pre-entry callers.
  if (!eventUserInfoRequest) {
    eventUserInfoRequest = eventUserInfoEventMembers(eventId ? { queryParams: { event: eventId } } : {});
  }

  try {
    return mapResponse((await eventUserInfoRequest) as EventUserInfoResponse);
  } catch (error) {
    eventUserInfoRequest = null;
    loggerService.error('event-user-info failed; falling back to legacy endpoint', error);
    return legacy();
  }
}
